import { Position } from './Position';
export var TerritoryState;
(function (TerritoryState) {
    TerritoryState["NEUTRAL"] = "neutral";
    TerritoryState["OWNED"] = "owned";
    TerritoryState["BURNED"] = "burned";
})(TerritoryState || (TerritoryState = {}));
export class Territory {
    _position;
    _state;
    _owner;
    _occupiedBy;
    _isActiveField;
    _previousOwner = null;
    _isScoringPenalized = false;
    _wasEnemyActiveField = false;
    constructor(_position, state = TerritoryState.NEUTRAL, owner = null, occupiedBy = null, isActiveField = false) {
        this._position = _position;
        this._state = state;
        this._owner = owner;
        this._occupiedBy = occupiedBy;
        this._isActiveField = isActiveField;
        // Валидация
        if (state === TerritoryState.NEUTRAL && owner !== null) {
            throw new Error('Neutral territory cannot have an owner');
        }
        if (state === TerritoryState.OWNED && owner === null) {
            throw new Error('Owned territory must have an owner');
        }
    }
    get position() {
        return this._position;
    }
    get state() {
        return this._state;
    }
    get owner() {
        return this._owner;
    }
    get occupiedBy() {
        return this._occupiedBy;
    }
    get isActiveField() {
        return this._isActiveField;
    }
    get isScoringPenalized() {
        return this._isScoringPenalized;
    }
    setIsScoringPenalized(value) {
        this._isScoringPenalized = value;
    }
    /**
     * Флаг: была ли территория активным полем врага на момент захвата
     * Используется для начисления +30 очков вместо +10.
     * #D2: выставляется ТОЛЬКО атомарно внутри capture(sideId, wasEnemyActiveField)
     * и сбрасывается при каждом (пере)захвате — поэтому публичного сеттера нет
     * (отдельный set до capture() и приводил к «залипшим» 30 очкам).
     */
    get wasEnemyActiveField() {
        return this._wasEnemyActiveField;
    }
    /**
     * Полный снимок изменяемого состояния клетки — для симуляций окружения.
     *
     * capture()/makeNeutral() как способ отката не годятся: они переписывают
     * previousOwner и сбрасывают wasEnemyActiveField, то есть симуляция «проверить,
     * что будет, если» безвозвратно портила уже начисленные очки на реальной доске.
     */
    snapshot() {
        return {
            owner: this._owner,
            previousOwner: this._previousOwner,
            state: this._state,
            occupiedBy: this._occupiedBy,
            isActiveField: this._isActiveField,
            isScoringPenalized: this._isScoringPenalized,
            wasEnemyActiveField: this._wasEnemyActiveField,
        };
    }
    /** Восстанавливает состояние из snapshot() без побочных эффектов. */
    restoreSnapshot(snap) {
        this._owner = snap.owner;
        this._previousOwner = snap.previousOwner;
        this._state = snap.state;
        this._occupiedBy = snap.occupiedBy;
        this._isActiveField = snap.isActiveField;
        this._isScoringPenalized = snap.isScoringPenalized;
        this._wasEnemyActiveField = snap.wasEnemyActiveField;
    }
    /**
     * Проверяет, можно ли захватить территорию
     */
    canBeCaptured() {
        return this._state === TerritoryState.NEUTRAL || this._state === TerritoryState.OWNED;
    }
    /**
     * Захват территории
     */
    capture(sideId, wasEnemyActiveField = false) {
        if (!this.canBeCaptured()) {
            throw new Error(`Territory at ${this._position} cannot be captured (state: ${this._state})`);
        }
        // Перезахват СВОЕЙ ЖЕ клетки — это не захват, а восстановление состояния.
        // checkSurroundingCaptures и wouldCompleteSurrounding зовут capture() на уже
        // своей клетке (откат симуляции окружения); сбрасывать здесь scoring-метаданные
        // означало стирать только что начисленные 30 очков за ходовое поле врага.
        // Именно поэтому захват окружением — единственный способ забрать чужую
        // территорию — почти всегда давал 10 вместо 30.
        const isSameOwnerRecapture = this._owner === sideId;
        // Сохраняем предыдущего владельца для подсчёта очков
        if (this._owner !== null && this._owner !== sideId) {
            this._previousOwner = this._owner;
        }
        this._owner = sideId;
        this._state = TerritoryState.OWNED;
        if (isSameOwnerRecapture) {
            return;
        }
        // Captured-active-field flag (#D2): set on EVERY capture from the caller's
        // live check, so it RESETS to false when a cell is recaptured from an owner
        // who no longer holds it as a live active field. Before, it was set true at
        // the call sites and never cleared, so a recaptured 30-pt cell stayed worth
        // 30 forever instead of 10 on recapture (design doc line 3346).
        this._wasEnemyActiveField = wasEnemyActiveField;
        // ✅ FIX: Don't reset penalty here - let CaptureEngine control it
        // this._isScoringPenalized = false; // REMOVED
    }
    get previousOwner() {
        return this._previousOwner;
    }
    /**
     * Выжигание территории (при паритете сил)
     */
    burn() {
        // 🔥 ДИАГНОСТИКА: Логируем stack trace для отслеживания вызова burn()
        console.error(`\n🔥🔥🔥 [Territory.burn] BURNING TERRITORY at ${this._position.toString()}`);
        console.error(`    Previous state: ${this._state}, owner: ${this._owner}, occupiedBy: ${this._occupiedBy}`);
        console.error('📍 Stack trace:');
        console.error(new Error().stack);
        console.error('🔥🔥🔥\n');
        this._state = TerritoryState.BURNED;
        this._owner = null;
        this._occupiedBy = null;
        this._isActiveField = false;
        this._isScoringPenalized = false;
        this._wasEnemyActiveField = false;
    }
    /**
     * Размещение фигуры на территории
     */
    occupy(sideId) {
        if (this._state === TerritoryState.BURNED) {
            throw new Error(`Cannot occupy burned territory at ${this._position}`);
        }
        // 🎯 ДИАГНОСТИКА: Логируем occupy колонок E,F (x=4,5)
        if ((this._position.x === 4 || this._position.x === 5) && this._position.y >= 3 && this._position.y <= 7) {
            console.error(`\n🎲 [Territory.occupy] ${this._position.toString()} occupied by Side ${sideId}`);
            console.error(`    owner=${this._owner}, state=${this._state}`);
        }
        this._occupiedBy = sideId;
    }
    /**
     * Удаление фигуры с территории
     */
    vacate() {
        // 🎯 ДИАГНОСТИКА: Логируем vacate колонок E,F (x=4,5)
        if ((this._position.x === 4 || this._position.x === 5) && this._position.y >= 3 && this._position.y <= 7) {
            console.error(`\n❌ [Territory.vacate] ${this._position.toString()} vacated`);
            console.error(`    owner=${this._owner}, occupiedBy=${this._occupiedBy} -> null, state=${this._state}`);
        }
        this._occupiedBy = null;
    }
    /**
     * Восстановление территории в нейтральное состояние
     * Используется для временных проверок (например, wouldCompleteSurrounding)
     */
    makeNeutral() {
        if (this._state === TerritoryState.BURNED) {
            throw new Error(`Cannot make burned territory neutral at ${this._position}`);
        }
        this._state = TerritoryState.NEUTRAL;
        this._owner = null;
        this._occupiedBy = null;
        this._isActiveField = false;
        this._isScoringPenalized = false;
        this._wasEnemyActiveField = false;
    }
    /**
     * Установка как активное поле
     */
    setAsActiveField(active) {
        if (this._state === TerritoryState.BURNED && active) {
            throw new Error('Cannot set burned territory as active field');
        }
        this._isActiveField = active;
    }
    /**
     * Проверка, является ли территория свободной для движения
     * @param _sideId - ID стороны (не используется, оставлен для совместимости API)
     *
     * ПРАВИЛО ADHESION:
     * - Можно ходить ТОЛЬКО на нейтральные территории (owner === null)
     * - Нельзя ходить на занятые территории (owner !== null)
     * - Нельзя ходить на территории с фигурами (occupiedBy !== null)
     * - Захват чужих территорий происходит ТОЛЬКО через окружение
     */
    isAvailableForMovement(_sideId) {
        // Нельзя ходить на сожженные территории
        if (this._state === TerritoryState.BURNED) {
            return false;
        }
        // Нельзя ходить на занятые территории (где есть владелец)
        // Захват чужих территорий - ТОЛЬКО через окружение!
        if (this._owner !== null) {
            return false;
        }
        // FIX BUG-6: Нельзя ходить на территории где стоит фигура
        // Даже если территория нейтральная, на ней может быть фигура
        // (например, при отмене хода или в особых игровых ситуациях)
        if (this._occupiedBy !== null) {
            return false;
        }
        // Можно ходить только на нейтральные незанятые территории
        return true;
    }
    /**
     * Клонирование территории (для иммутабельности)
     */
    clone() {
        const t = new Territory(this._position, this._state, this._owner, this._occupiedBy, this._isActiveField);
        t._previousOwner = this._previousOwner;
        t._isScoringPenalized = this._isScoringPenalized;
        t._wasEnemyActiveField = this._wasEnemyActiveField;
        return t;
    }
    /**
     * Экспорт данных территории
     */
    toData() {
        return {
            position: this._position,
            state: this._state,
            owner: this._owner,
            occupiedBy: this._occupiedBy,
            isActiveField: this._isActiveField,
            previousOwner: this._previousOwner,
            isScoringPenalized: this._isScoringPenalized,
            wasEnemyActiveField: this._wasEnemyActiveField,
        };
    }
    /**
     * Сериализация в JSON
     */
    toJSON() {
        // 🎯 ДИАГНОСТИКА: Логируем JSON для колонок E,F (x=4,5)
        if ((this._position.x === 4 || this._position.x === 5) && this._position.y >= 3 && this._position.y <= 7) {
            console.error(`\n📤 [Territory.toJSON] ${this._position.toString()} serializing: owner=${this._owner}, occupiedBy=${this._occupiedBy}, state=${this._state}`);
        }
        return {
            position: {
                x: this._position.x,
                y: this._position.y,
            },
            state: this._state,
            owner: this._owner,
            occupiedBy: this._occupiedBy,
            isActiveField: this._isActiveField,
            previousOwner: this._previousOwner,
            isScoringPenalized: this._isScoringPenalized,
            wasEnemyActiveField: this._wasEnemyActiveField,
        };
    }
    /**
     * Восстановление из JSON
     */
    static fromJSON(data) {
        const position = new Position(data.position.x, data.position.y);
        const territory = new Territory(position, data.state, data.owner, data.occupiedBy, data.isActiveField);
        if (data.previousOwner !== undefined) {
            territory._previousOwner = data.previousOwner;
        }
        if (data.isScoringPenalized !== undefined) {
            territory._isScoringPenalized = data.isScoringPenalized;
        }
        if (data.wasEnemyActiveField !== undefined) {
            territory._wasEnemyActiveField = data.wasEnemyActiveField;
        }
        return territory;
    }
    toString() {
        return `Territory[${this._position}, state=${this._state}, owner=${this._owner}, occupied=${this._occupiedBy}]`;
    }
}
//# sourceMappingURL=Territory.js.map